<template>
  <div class="public-exhibitors-list">
    <div v-if="loading" class="loading">
      <p>{{ t.loading_exhibitors }}</p>
    </div>

    <div v-else-if="error" class="error">
      <p>{{ t.error_loading_exhibitors }}</p>
    </div>

    <div v-else class="exhibitors-table-wrapper">
      <div v-if="exhibitors.length > 0" class="exhibitors-summary">
        <p>{{ t.showing_exhibitors }}: {{ filteredExhibitors.length }} {{ t.of }} {{ totalExhibitors }}</p>
      </div>

      <div class="search-exhibitors">
        <span class="header-filter">
            <input
                v-model="searchTerm"
                :placeholder="t.search_company || 'Zoek bedrijfsnaam...'"
                class="filter-input"
                @input="debouncedSearch"
                @click.stop
            >
            <button
                v-if="searchTerm"
                @click="clearSearch"
                class="clear-filter-button"
                :title="t.clear_search"
            >
              ×
            </button>
          </span>
      </div>
      <table class="exhibitors-table">
        <thead>
          <tr>
            <th class="company-header">
              <div class="header-content">
                <span class="header-label" @click="sort('company_name')" :class="{ active: sortBy === 'company_name' }">
                  {{ t.company_name || 'Standhouder' }}
                  <span v-if="sortBy === 'company_name'" class="sort-indicator">
                    {{ sortOrder === 'asc' ? '&#x42;' : '&#x43;' }}
                  </span>
                </span>
              </div>
            </th>
            <th class="stand-header">
              <div class="header-content">
                <div class="header-label">
                  {{ t.stand_number || 'Standnummer' }}
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(exhibitor, index) in filteredExhibitors" :key="exhibitor.id" class="exhibitor-row" :class="{ 'zebra-even': index % 2 === 0, 'zebra-odd': index % 2 === 1 }">
            <td class="company-name">
              <div class="exhibitor-names">
                <template v-if="exhibitor.exhibitors_with_websites && exhibitor.exhibitors_with_websites.length > 0">
                  <template v-for="(exhibitorData, exhibitorIndex) in exhibitor.exhibitors_with_websites" :key="exhibitorIndex">
                    <span v-if="exhibitorIndex > 0" class="exhibitor-separator">, </span>
                    <a
                      v-if="exhibitorData.website && exhibitorData.website.trim()"
                      :href="formatWebsiteUrl(exhibitorData.website)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="exhibitor-link"
                    >
                      {{ exhibitorData.name }}
                    </a>
                    <span v-else class="exhibitor-name">{{ exhibitorData.name }}</span>
                  </template>
                </template>
                <span v-else class="exhibitor-name">{{ exhibitor.company_name }}</span>
              </div>
            </td>
            <td class="stand-number">{{ exhibitor.stand_numbers || exhibitor.stand_number }}</td>
          </tr>
        </tbody>
      </table>

      <div v-if="filteredExhibitors.length === 0 && !loading" class="no-results">
        <p>{{ t.no_exhibitors_found }}</p>
        <button v-if="searchTerm" @click="clearSearch" class="clear-search-link">
          {{ t.clear_search_and_show_all }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import useTexts from '@/composables/useTexts';
import { formatWebsiteUrl } from '@/composables/useUtilityFunctions';

export default {
  name: 'PublicExhibitorsList',
  props: {
    initialSortBy: {
      type: String,
      default: 'company_name'
    },
    initialSortOrder: {
      type: String,
      default: 'asc'
    }
  },
  setup(props) {
    const { getTextsForComponent, fetchTexts } = useTexts();
    const t = getTextsForComponent('PublicExhibitorsList');

    // Reactive data
    const exhibitors = ref([]);
    const filteredExhibitors = ref([]);
    const totalExhibitors = ref(0);
    const loading = ref(false);
    const error = ref(false);
    const searchTerm = ref('');
    const sortBy = ref(props.initialSortBy);
    const sortOrder = ref(props.initialSortOrder);

    // Debounced search
    let searchTimeout = null;
    const debouncedSearch = () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        // Filter locally instead of refetching
        filterExhibitors();
      }, 300);
    };

    // Filter exhibitors locally
    const filterExhibitors = () => {
      if (!searchTerm.value.trim()) {
        filteredExhibitors.value = exhibitors.value;
      } else {
        const searchLower = searchTerm.value.toLowerCase();
        filteredExhibitors.value = exhibitors.value.filter(exhibitor => {
          // Search in main company name
          if (exhibitor.company_name.toLowerCase().includes(searchLower)) {
            return true;
          }

          // Search in additional exhibitors
          if (exhibitor.additional_exhibitors && exhibitor.additional_exhibitors.length > 0) {
            return exhibitor.additional_exhibitors.some(additional =>
              additional.name.toLowerCase().includes(searchLower)
            );
          }

          return false;
        });
      }
    };

    // Methods
    const fetchExhibitors = async () => {
      loading.value = true;
      error.value = false;

      try {
        const params = new URLSearchParams({
          search: '', // Always fetch all data
          sort_by: sortBy.value,
          sort_order: sortOrder.value
        });

        const response = await fetch(`${window.rvr_public.api_url}/public/exhibitors?${params}`);

        if (!response.ok) {
          throw new Error('Failed to fetch exhibitors');
        }

        const data = await response.json();
        exhibitors.value = data.exhibitors || [];
        totalExhibitors.value = data.total || 0;

        // Apply local filtering after fetching
        filterExhibitors();
      } catch (err) {
        console.error('Error fetching exhibitors:', err);
        error.value = true;
        exhibitors.value = [];
        filteredExhibitors.value = [];
        totalExhibitors.value = 0;
      } finally {
        loading.value = false;
      }
    };

    const sort = (field) => {
      // Only allow sorting by company_name
      if (field !== 'company_name') {
        return;
      }

      if (sortBy.value === field) {
        toggleSortOrder();
      } else {
        sortBy.value = field;
        sortOrder.value = 'asc';
        fetchExhibitors();
      }
    };

    const toggleSortOrder = () => {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
      fetchExhibitors();
    };

    const clearSearch = () => {
      searchTerm.value = '';
      filterExhibitors();
    };

    // Watchers
    watch([sortBy, sortOrder], () => {
      fetchExhibitors();
    });

    // Lifecycle
    onMounted(async () => {
      // Fetch texts first
      await fetchTexts(window.rvr_public?.lang || 'nl');
      // Then fetch exhibitors
      fetchExhibitors();
    });

    return {
      t,
      exhibitors,
      filteredExhibitors,
      totalExhibitors,
      loading,
      error,
      searchTerm,
      sortBy,
      sortOrder,
      debouncedSearch,
      fetchExhibitors,
      sort,
      toggleSortOrder,
      clearSearch,
      formatWebsiteUrl
    };
  }
};
</script>

<style scoped>

/* Header styles */
.company-header {
  width: 80%;
}
.stand-header {
  width: 20%;
}
.header-content {
  display: flex;
  flex-direction: row;
  gap: 8px;
}

.header-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 0;
  transition: color 0.3s ease;
}

.header-filter {
  position: relative;
}

.filter-input {
  width: 100%;
  padding: 8px 32px 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.filter-input:focus {
  outline: none;
  border-color: #007cba;
}

.clear-filter-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 16px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-filter-button:hover {
  color: #333;
}

.exhibitors-summary {
  margin-bottom: 15px;
  color: #666;
  font-size: 14px;
}

.exhibitors-table-wrapper {
  overflow-x: auto;
}

.exhibitors-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.exhibitors-table th {
  background: #f8f9fa;
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #dee2e6;
}

.sort-indicator {
  font-size: 18px;
  font-family: ETmodules !important;
}

.exhibitors-table td {
  padding: 16px 12px;
  border-bottom: 1px solid #dee2e6;
  vertical-align: top;
}

.exhibitor-row:hover {
  background: #f8f9fa;
}

/* Zebra striping */
.zebra-even {
  background: #ffffff;
}

.zebra-odd {
  background: #f8f9fa;
}

.zebra-even:hover,
.zebra-odd:hover {
  background: #e9ecef;
}

.company-name {
  font-weight: 600;
  color: #333;
}

.exhibitor-names {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.exhibitor-name {
  display: inline;
  margin-bottom: 4px;
}

.exhibitor-separator {
  color: #666;
  margin: 0 2px;
}

.stand-number {
  font-weight: 600;
  min-width: 80px;
}

.loading, .error, .no-results {
  text-align: center;
  padding: 40px 20px;
}

.error {
  color: #d63638;
}

.clear-search-link {
  background: none;
  border: none;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  margin-top: 10px;
}

/* Responsive design */
@media (max-width: 768px) {

  .stand-header .header-label {
    display: none;
    visibility: hidden;
  }
  .stand-header .header-content:after {
    content: "Nr";
    display: block;
  }
  .stand-number {
    min-width: auto !important;
  }
  .exhibitors-table {
    font-size: 14px;
  }

  .exhibitors-table th,
  .exhibitors-table td {
    padding: 12px 8px;
  }

  .header-content {
    gap: 6px;
  }

  .filter-input {
    font-size: 13px;
    padding: 6px 28px 6px 10px;
  }
}

@media (max-width: 480px) {
  .exhibitors-header h2 {
    font-size: 1.5rem;
  }

  .exhibitors-table th,
  .exhibitors-table td {
    padding: 8px 6px;
  }

  .header-label {
    font-size: 14px;
  }

  .filter-input {
    font-size: 12px;
    padding: 5px 24px 5px 8px;
  }
}
</style>
